import React, { useEffect, useState } from "react";
import { useNoIndex } from "@/lib/usePageSeo";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { LogOut, Pencil, Trash2, Plus, GripVertical, Star } from "lucide-react";
import { toast } from "sonner";

// Panels + drawers (extracted from this file — see /pages/admin)
import { ServiceEditorDrawer } from "@/pages/admin/ServiceEditorDrawer";
import { PortfolioEditorDrawer } from "@/pages/admin/PortfolioEditorDrawer";
import { SetupEditorDrawer } from "@/pages/admin/SetupEditorDrawer";
import { SoftwareEditorDrawer } from "@/pages/admin/SoftwareEditorDrawer";
import { SoftwareRequestsPanel } from "@/pages/admin/SoftwareRequestsPanel";
import { ServiceQuoteRequestsPanel } from "@/pages/admin/ServiceQuoteRequestsPanel";
import { VolumeLaptopRequestsPanel } from "@/pages/admin/VolumeLaptopRequestsPanel";
import { LegacyOrderAuditPanel } from "@/pages/admin/LegacyOrderAuditPanel";
import { ServicesArchivePanel } from "@/pages/admin/ServicesArchivePanel";
import { WorkstationsAdminPanel } from "@/pages/admin/WorkstationsAdminPanel";
import { EditDrawer } from "@/pages/admin/EditDrawer";
import { OrdersPanel } from "@/pages/admin/OrdersPanel";
import { EmailIntakePanel } from "@/pages/admin/EmailIntakePanel";
import { SiteSettingsPanel } from "@/pages/admin/SiteSettingsPanel";
import { PresentationPanel } from "@/pages/admin/PresentationPanel";
import { PopularAuditBanner } from "@/pages/admin/PopularAuditBanner";
import { ImageUploadField } from "@/pages/admin/fields/ImageUploadField";
import { BrandedLoader } from "@/components/util/BrandedLoader";
import { OperationsHome } from "@/pages/admin/operations/OperationsHome";
import { JobsPanel as OperationsJobsPanel } from "@/pages/admin/operations/JobsPanel";
import { CustomersPanel as OperationsCustomersPanel } from "@/pages/admin/operations/CustomersPanel";

const RESOURCES = [
  { key: "services", label: "Services", fields: ["slug", "name", "icon", "image", "short_description", "featured"], editorType: "service" },
  { key: "portfolio", label: "Portfolio", fields: ["slug", "title", "client", "category", "image"], editorType: "portfolio" },
  { key: "laptops", label: "Creator Setups", fields: ["slug", "name", "persona", "price", "image", "icon_url", "byline", "short_description", "long_description"], richEditor: true },
  { key: "software", label: "Software", fields: ["name", "slug", "category", "publisher"], editorType: "software" },
  { key: "brands", label: "Brands", fields: ["name", "logo", "row", "active", "display_order"] },
  { key: "testimonials", label: "Testimonials", fields: ["quote", "author_name", "author_role", "image"] },
];

// Beast V1 Checkpoint 4 — four-section information architecture.
// Operations is the default section post-login. Every existing tab
// key is preserved so admin-tab-* testids and prior functionality
// remain intact — they just live behind a section switcher now.
const SECTIONS = [
  {
    key: "operations",
    label: "Operations",
    tabs: [
      { key: "home", label: "Home" },
      { key: "jobs", label: "Jobs" },
      { key: "customers", label: "Customers" },
      { key: "email-intake", label: "Email Intake" },
    ],
  },
  {
    key: "commerce",
    label: "Commerce & Inquiries",
    tabs: [
      { key: "orders", label: "Orders" },
      { key: "order-audit", label: "Order Audit" },
      { key: "service-quotes", label: "Service Quotes" },
      { key: "software-requests", label: "Software Requests" },
      { key: "volume-laptops", label: "Volume Laptops" },
    ],
  },
  {
    key: "website",
    label: "Website",
    tabs: [
      { key: "services", label: "Services" },
      { key: "portfolio", label: "Portfolio" },
      { key: "workstations", label: "Workstations" },
      { key: "laptops", label: "Creator Setups" },
      { key: "software", label: "Software" },
      { key: "brands", label: "Brands" },
      { key: "testimonials", label: "Testimonials" },
    ],
  },
  {
    key: "configuration",
    label: "Configuration",
    tabs: [
      { key: "services-archive", label: "Services Archive" },
      { key: "settings", label: "Site Settings" },
      { key: "presentation", label: "Private Presentation" },
    ],
  },
];

const TAB_TO_SECTION = SECTIONS.reduce((acc, s) => {
  s.tabs.forEach((t) => {
    acc[t.key] = s.key;
  });
  return acc;
}, {});

export default function AdminDashboardPage() {
  useNoIndex();
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  // Beast V1 CP4 — always land on Operations Home post-login. Do NOT
  // restore a previous CMS tab.
  const [section, setSection] = useState("operations");
  const [tab, setTab] = useState("home");
  // When an admin clicks "View Job" from an Email Intake, we route to
  // Operations → Jobs and pass the job id through so the drawer opens
  // automatically.
  const [focusJobId, setFocusJobId] = useState(null);
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [intakeCounts, setIntakeCounts] = useState(null); // null = feature off
  // Service Quotes attention-needed count (Mar 2026). Mirrors the
  // Email Intake pattern: fetched on admin mount + whenever the
  // dashboard tab changes to `service-quotes` so a status update
  // decrements the badge instantly. Falls back to 0 on any error.
  const [serviceQuoteNewCount, setServiceQuoteNewCount] = useState(0);
  const unseenOrders = orders.filter((o) => !o.admin_seen_at).length;
  // Callback passed to OrdersPanel — flip a single order to seen in
  // local state the moment an admin opens it, so the tab badge + row
  // marker update instantly without a full refetch.
  const markOrderSeenLocally = (orderId) => {
    setOrders((prev) => prev.map((o) => (
      o.id === orderId && !o.admin_seen_at
        ? { ...o, admin_seen_at: new Date().toISOString() }
        : o
    )));
  };
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const [services, setServices] = useState([]);

  // Section-aware tab setter — updates both the section and the tab
  // atomically so the sub-nav stays consistent.
  const goToTab = (nextTab) => {
    const nextSection = TAB_TO_SECTION[nextTab] || "operations";
    setSection(nextSection);
    setTab(nextTab);
  };

  const openJobFromIntake = (jobId) => {
    setFocusJobId(jobId);
    goToTab("jobs");
  };

  useEffect(() => {
    if (!loading && !user) navigate("/admin");
    if (!loading && user && !user.is_admin) navigate("/admin?error=forbidden");
  }, [user, loading, navigate]);

  // Portfolio editor needs the services list so it can render service tags
  // as toggleable chips. Loaded once when the admin is authenticated.
  useEffect(() => {
    if (user?.is_admin) {
      api.get("/services").then((r) => setServices(r.data)).catch(() => {});
      // Beast V1 CP4 — orders are needed for the Commerce → Orders
      // unseen badge regardless of which tab is active on landing.
      api
        .get("/admin/orders")
        .then((r) => setOrders(r.data))
        .catch(() => {});
      // Probe the Email Intake counts endpoint — presence of a 200
      // response is our signal that EMAIL_INTAKE_ENABLED is truthy on
      // the backend and the tab should render. Anything else (404 when
      // the router isn't mounted, network error, etc.) hides the tab.
      api
        .get("/admin/email-intakes/counts")
        .then((r) => setIntakeCounts(r.data))
        .catch(() => setIntakeCounts(null));
      // Service Quotes attention-needed count — see comment above.
      api
        .get("/admin/service-quote-requests/new-count")
        .then((r) => setServiceQuoteNewCount(r.data?.count || 0))
        .catch(() => setServiceQuoteNewCount(0));
    }
  }, [user]);

  // Refresh the Service Quotes count whenever the admin lands on
  // the tab or moves away (a status update while inside the panel
  // should decrement the badge on tab switch).
  const refreshServiceQuoteCount = () => {
    api
      .get("/admin/service-quote-requests/new-count")
      .then((r) => setServiceQuoteNewCount(r.data?.count || 0))
      .catch(() => {});
  };
  useEffect(() => {
    if (user?.is_admin) refreshServiceQuoteCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, user]);

  const refreshIntakeCounts = () => {
    if (intakeCounts === null) return;
    api
      .get("/admin/email-intakes/counts")
      .then((r) => setIntakeCounts(r.data))
      .catch(() => {});
  };

  const load = async () => {
    if (
      tab === "home" ||
      tab === "jobs" ||
      tab === "customers" ||
      tab === "orders" ||
      tab === "order-audit" ||
      tab === "settings" ||
      tab === "presentation" ||
      tab === "email-intake" ||
      tab === "software-requests" ||
      tab === "service-quotes" ||
      tab === "volume-laptops" ||
      tab === "services-archive" ||
      tab === "workstations"
    )
      return;
    // Portfolio needs the admin endpoint so draft projects are visible in the
    // dashboard list. Every other resource uses its public list unchanged.
    const listPath = tab === "portfolio" ? "/admin/portfolio" : `/${tab}`;
    const res = await api.get(listPath);
    setItems(res.data);
    if (user?.is_admin) {
      try {
        const o = await api.get("/admin/orders");
        setOrders(o.data);
      } catch {}
    }
  };

  useEffect(() => {
    if (user?.is_admin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, user]);

  const resource = RESOURCES.find((r) => r.key === tab);

  // Portfolio editor stays open on Save (Feb 2026 owner request):
  // callers may pass `{ keepOpen: true }` to skip the auto-close side
  // effect and receive the persisted document back so the drawer can
  // sync its local state (id, slug, display_order) and turn subsequent
  // Saves into UPDATE calls instead of duplicate CREATEs. All other
  // editors continue to close on Save exactly as before (no opts).
  const save = async (payload, opts = {}) => {
    try {
      let saved;
      if (creating) {
        const res = await api.post(`/admin/${tab}`, payload);
        saved = res.data;
        toast.success("Created");
      } else {
        const res = await api.put(`/admin/${tab}/${payload.id}`, payload);
        saved = res.data;
        toast.success("Saved");
      }
      if (opts.keepOpen) {
        // Editor stays open; flip out of create-mode so the next Save
        // on the same session targets the newly persisted document.
        setCreating(false);
      } else {
        setEditing(null);
        setCreating(false);
      }
      load();
      return saved;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      throw e;
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this item?")) return;
    try {
      await api.delete(`/admin/${tab}/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  // Drag-and-drop reordering
  const onDragStart = (i) => (e) => {
    setDragIndex(i);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", String(i)); } catch {}
  };
  const onDragOver = (i) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (overIndex !== i) setOverIndex(i);
  };
  const onDragEnd = () => {
    setDragIndex(null);
    setOverIndex(null);
  };
  const onDrop = (i) => async (e) => {
    e.preventDefault();
    const from = dragIndex;
    const to = i;
    setDragIndex(null);
    setOverIndex(null);
    if (from === null || from === to) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    // Optimistic update
    setItems(next);
    try {
      await api.post(`/admin/${tab}/reorder`, { ordered_ids: next.map((x) => x.id) });
      toast.success("Order saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reorder failed");
      load();
    }
  };

  if (loading || !user) {
    return <BrandedLoader testid="admin-branded-loader" />;
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0F0F0F] text-white">
      <header className="border-b border-[#1F1F1F] bg-[#0A0A0A] px-6 py-4 flex items-center justify-between">
        <div>
          <div className="ur-eyebrow">Ur Designs CMS</div>
          <div className="text-sm mt-1">Welcome, <span className="font-semibold">{user.name}</span></div>
        </div>
        <button data-testid="admin-logout" onClick={async () => { await logout(); navigate("/"); }} className="ur-btn-secondary text-sm">
          Logout <LogOut className="w-4 h-4" />
        </button>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 py-10">
        {/* Section switcher — 4 top-level areas per Beast V1 CP4 */}
        <div
          data-testid="admin-section-switcher"
          className="flex flex-wrap gap-2 mb-4"
        >
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              data-testid={`admin-section-${s.key}`}
              onClick={() => {
                setSection(s.key);
                setTab(s.tabs[0].key);
              }}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${
                section === s.key
                  ? "bg-white text-[#0F0F0F]"
                  : "bg-[#1A1A1A] border border-[#262626] text-white hover:border-[#FF6600]"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Sub-tab pills — only the current section's tabs render */}
        <div className="flex flex-wrap gap-2 mb-6">
          {SECTIONS.find((s) => s.key === section)?.tabs.map((t) => {
            // Hide Email Intake sub-tab entirely when the backend
            // feature flag is off (probe returned null).
            if (t.key === "email-intake" && intakeCounts === null) return null;
            const active = tab === t.key;
            const badge =
              t.key === "orders" && unseenOrders > 0 ? (
                <span
                  data-testid="admin-orders-unseen-badge"
                  className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold ${
                    active ? "bg-white text-[#FF6600]" : "bg-[#FF6600] text-white"
                  }`}
                >
                  {unseenOrders}
                </span>
              ) : t.key === "service-quotes" && serviceQuoteNewCount > 0 ? (
                <span
                  data-testid="admin-service-quotes-new-badge"
                  className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold ${
                    active ? "bg-white text-[#FF6600]" : "bg-[#FF6600] text-white"
                  }`}
                >
                  {serviceQuoteNewCount}
                </span>
              ) : t.key === "email-intake" && intakeCounts?.actionable > 0 ? (
                <span
                  data-testid="admin-email-intake-badge"
                  className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold ${
                    active ? "bg-white text-[#FF6600]" : "bg-[#FF6600] text-white"
                  }`}
                >
                  {intakeCounts.actionable}
                </span>
              ) : null;
            return (
              <button
                key={t.key}
                data-testid={`admin-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors inline-flex items-center gap-2 ${
                  active
                    ? "bg-[#FF6600] text-white"
                    : "bg-[#1A1A1A] border border-[#262626] text-white hover:border-[#FF6600]"
                }`}
              >
                {t.label}
                {badge}
              </button>
            );
          })}
        </div>

        {tab === "home" ? (
          <OperationsHome
            user={user}
            onNavigate={goToTab}
            focusJobId={focusJobId}
            onFocusHandled={() => setFocusJobId(null)}
          />
        ) : tab === "jobs" ? (
          <OperationsJobsPanel
            focusJobId={focusJobId}
            onFocusHandled={() => setFocusJobId(null)}
          />
        ) : tab === "customers" ? (
          <OperationsCustomersPanel />
        ) : tab === "settings" ? (
          <SiteSettingsPanel />
        ) : tab === "presentation" ? (
          <PresentationPanel />
        ) : tab === "email-intake" ? (
          <EmailIntakePanel
            key={intakeCounts?.actionable ?? 0}
            onRefreshCounts={refreshIntakeCounts}
            onOpenJob={openJobFromIntake}
          />
        ) : tab === "orders" ? (
          <OrdersPanel orders={orders} onMarkSeen={markOrderSeenLocally} />
        ) : tab === "order-audit" ? (
          <LegacyOrderAuditPanel />
        ) : tab === "software-requests" ? (
          <SoftwareRequestsPanel />
        ) : tab === "service-quotes" ? (
          <ServiceQuoteRequestsPanel />
        ) : tab === "volume-laptops" ? (
          <VolumeLaptopRequestsPanel />
        ) : tab === "services-archive" ? (
          <ServicesArchivePanel />
        ) : tab === "workstations" ? (
          <WorkstationsAdminPanel />
        ) : resource ? (
          <>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl font-bold">{resource.label}</h2>
              <button
                data-testid={`admin-create-${tab}`}
                onClick={() => { setCreating(true); setEditing({}); }}
                className="ur-btn-primary text-sm py-2.5 px-5"
              >
                <Plus className="w-4 h-4" /> New
              </button>
            </div>

            {tab === "services" && <PopularAuditBanner services={items} />}

            <div className="grid gap-3" data-testid={`admin-list-${tab}`}>
              {items.map((it, i) => (
                <div
                  key={it.id}
                  data-testid={`admin-item-${it.id}`}
                  data-drag-index={i}
                  draggable
                  onDragStart={onDragStart(i)}
                  onDragOver={onDragOver(i)}
                  onDrop={onDrop(i)}
                  onDragEnd={onDragEnd}
                  className={`ur-card p-4 flex items-center gap-4 transition-all ${
                    dragIndex === i ? "opacity-40" : ""
                  } ${overIndex === i && dragIndex !== i ? "border-[#FF6600] ring-1 ring-[#FF6600]" : ""}`}
                >
                  <span
                    data-testid={`admin-drag-handle-${it.id}`}
                    className="cursor-grab active:cursor-grabbing text-[#5A5A5A] hover:text-[#FF6600] shrink-0"
                    title="Drag to reorder"
                  >
                    <GripVertical className="w-5 h-5" />
                  </span>
                  {(it.image || it.logo) && (
                    <img src={it.image || it.logo} alt="" className="w-14 h-14 rounded-lg object-cover bg-[#0F0F0F]" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="font-semibold truncate">{it.name || it.title}</div>
                      {tab === "services" && it.featured && (
                        <span
                          data-testid={`admin-featured-badge-${it.id}`}
                          title="Shown on the homepage services row"
                          className="shrink-0 inline-flex items-center gap-1 rounded-full bg-[#FF6600]/12 border border-[#FF6600]/40 text-[#FF6600] text-[10px] font-bold tracking-[0.14em] uppercase px-2 py-0.5"
                        >
                          <Star className="w-3 h-3 fill-[#FF6600]" />
                          Home
                        </span>
                      )}
                      {tab === "portfolio" && (
                        (it.status === "draft") ? (
                          <span
                            data-testid={`admin-status-badge-${it.id}`}
                            title="Draft — hidden from public site until published"
                            className="shrink-0 inline-flex items-center rounded-full bg-[#F59E0B]/12 border border-[#F59E0B]/50 text-[#F59E0B] text-[10px] font-bold tracking-[0.14em] uppercase px-2 py-0.5"
                          >
                            Draft
                          </span>
                        ) : (
                          <span
                            data-testid={`admin-status-badge-${it.id}`}
                            title="Published — visible on the public site"
                            className="shrink-0 inline-flex items-center rounded-full bg-emerald-500/10 border border-emerald-500/40 text-emerald-400 text-[10px] font-bold tracking-[0.14em] uppercase px-2 py-0.5"
                          >
                            Published
                          </span>
                        )
                      )}
                      {tab === "brands" && (
                        <>
                          <span
                            data-testid={`admin-brand-row-badge-${it.id}`}
                            title="Which marquee row this logo belongs to"
                            className="shrink-0 inline-flex items-center rounded-full bg-[#FF6600]/12 border border-[#FF6600]/40 text-[#FF6600] text-[10px] font-bold tracking-[0.14em] uppercase px-2 py-0.5"
                          >
                            Row {Number(it.row) === 2 ? 2 : 1}
                          </span>
                          {it.active === false && (
                            <span
                              data-testid={`admin-brand-inactive-badge-${it.id}`}
                              title="Hidden from the homepage marquee"
                              className="shrink-0 inline-flex items-center rounded-full bg-[#5A5A5A]/20 border border-[#5A5A5A]/60 text-[#A3A3A3] text-[10px] font-bold tracking-[0.14em] uppercase px-2 py-0.5"
                            >
                              Inactive
                            </span>
                          )}
                        </>
                      )}
                    </div>
                    <div className="text-xs text-[#A3A3A3] truncate">{it.slug || it.category || it.persona || it.subtitle}</div>
                  </div>
                  <button data-testid={`admin-edit-${it.id}`} onClick={() => { setCreating(false); setEditing(it); }} className="w-9 h-9 rounded-full border border-[#262626] grid place-items-center hover:border-[#FF6600]"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`admin-delete-${it.id}`} onClick={() => del(it.id)} className="w-9 h-9 rounded-full border border-[#262626] grid place-items-center hover:border-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              {items.length === 0 && <div className="text-[#A3A3A3] text-sm py-10 text-center">No items yet.</div>}
            </div>
          </>
        ) : null}
      </div>

      {editing && (
        (resource?.editorType === "service" ? (
          <ServiceEditorDrawer
            item={editing}
            onClose={() => { setEditing(null); setCreating(false); }}
            onSave={save}
            ImageUploadField={ImageUploadField}
          />
        ) : resource?.editorType === "portfolio" ? (
          <PortfolioEditorDrawer
            item={editing}
            services={services}
            onClose={() => { setEditing(null); setCreating(false); }}
            onSave={save}
          />
        ) : resource?.editorType === "software" ? (
          <SoftwareEditorDrawer
            item={editing}
            onClose={() => { setEditing(null); setCreating(false); }}
            onSave={save}
          />
        ) : resource?.richEditor ? (
          <SetupEditorDrawer
            item={editing}
            onClose={() => { setEditing(null); setCreating(false); }}
            onSave={save}
          />
        ) : (
          <EditDrawer
            item={editing}
            fields={resource?.fields || []}
            resourceKey={tab}
            onClose={() => { setEditing(null); setCreating(false); }}
            onSave={save}
          />
        ))
      )}
    </div>
  );
}
